<script lang="ts">
  import Layout from "$app/Layouts/University.svelte"
  import { onMount } from "svelte"
  import { Pulse } from "svelte-loading-spinners"
  import Modal from "$app/components/Modal.svelte"
  import { xhr, convertObjectToUrl, toast } from "$app/utils"
  import Book from "./Book.svelte"
  import BookForm from "./BookForm.svelte"
  import BookReadingsForm from "./BookReadingsForm.svelte"
  import ViewBook from "./ViewBook.svelte"
  import { ArrowLeft } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"

  type BookFilterType = {
    plan_filter: "all" | "plan" | "read_later"
    completed: boolean
    categories: string[]
    values: string[]
    search_query: string
  }

  let {
    plan,
    plan_books,
    current_book,
    categories,
    values,
    current_user,
    admin_university = false,
    plan_users,
    filters = {
      plan_filter: "all",
      completed: false,
      categories: [],
      values: [],
      search_query: ""
    },
    app_banner,
    onboarding
  }: {
    plan: PlanType
    plan_books: PlanBookType[]
    current_book: BookType
    categories: string[]
    values: string[]
    current_user: UserType
    admin_university?: boolean
    plan_users?: UserType[]
    filters?: BookFilterType
    app_banner: any
    onboarding: any
  } = $props()

  let openBookModal = $state(false)
  let openAddBookModal = $state(false)
  let addBookStep = $state(1)
  let addedBook = $state(null)
  let books = $state([])
  let book = $state(null)
  let bookFilters = $state(filters)

  function updatePlanFilter(plan_filter: "all" | "plan" | "read_later") {
    bookFilters.plan_filter = plan_filter
    getBooks(bookFilters, true)
  }

  function updateCategoryFilter() {
    bookFilters.categories = selectedCategories
    getBooks(bookFilters, true)
  }

  function updateValueFilter() {
    bookFilters.values = selectedValues
    getBooks(bookFilters, true)
  }

  function updateCompletedFilter() {
    bookFilters.completed = !bookFilters.completed
    getBooks(bookFilters, true)
  }

  function updateSearchQuery(search_query: string) {
    bookFilters.search_query = search_query
    getBooks(bookFilters, true)
  }

  let current_plan_book = null
  if (current_book) {
    current_plan_book = plan_books.find(function (pb) {
      if (pb.book_id === current_book.id) {
        return true
      }
    })
  }
  if (current_plan_book) {
    current_book = { ...current_book, plan_book: current_plan_book }
  }

  appStore.current_user = current_user
  appStore.plan = plan
  appStore.current_book = current_book
  appStore.admin_university = admin_university

  let selectedCategories = $state([])
  let selectedValues = $state([])
  let search_query = $state("")

  let isLoading = $state(false)
  let page = $state(1)
  let totalPages = $state(1)
  let vis = $state(true)

  function checkInfiniteLoad(): void {
    const totalPageHeight = document.body.scrollHeight
    const scrollPoint = window.scrollY + window.innerHeight + 500

    const visible = scrollPoint >= totalPageHeight

    if (visible && vis) {
      vis = false
      getBooks(bookFilters)
      setTimeout(() => (vis = true), 1000)
    }
  }

  onMount(() => {
    isLoading = true

    getBooks(filters)

    let prev = new Date().getTime()
    let now = 0
    const delay = 10
    window.onscroll = function () {
      now = new Date().getTime()
      if (now - prev > delay) {
        checkInfiniteLoad()
        prev = now
      }
    }
  })

  // Get all statements for this section
  async function getBooks(
    filter: BookFilterType,
    reset = false
  ): Promise<void> {
    if (reset) {
      totalPages = 0
      page = 1
      books = []
    }
    isLoading = true

    if (totalPages && page > totalPages) {
      isLoading = false
      return
    }

    try {
      const queryStr = convertObjectToUrl(filter)
      const url = `/plans/${appStore.plan.id}/books/filter.json?${queryStr}&page=${page}`
      const resp = await xhr.get(url)
      totalPages = +resp.headers["total-pages"]
      page++

      let new_books = resp.data
      new_books = new_books.map((b) => {
        let plan_book = plan_books.find(function (pb) {
          if (pb.book_id === b.id) {
            return true
          }
        })
        return { ...b, plan_book: plan_book }
      })

      books = [...books, ...new_books]

      window.history.pushState({}, "books", `books?${queryStr}`)
    } catch (e) {
      isLoading = false
      toast("There was an error loading the books", "error")
    } finally {
      setTimeout(() => {
        isLoading = false
      }, 1000)
    }
  }

  function viewBook(data: BookType) {
    book = data
    openBookModal = true
  }

  function addNewBook(book: BookType) {
    addBookStep = 2
    addedBook = book
  }

  function loadMyPlanBooks() {
    bookFilters = {
      plan_filter: "plan",
      completed: false,
      categories: [],
      values: [],
      search_query: ""
    }
    getBooks(bookFilters, true)
  }

  function addedBookReadings() {
    addBookStep = 1
    addedBook = null
    openAddBookModal = false
    loadMyPlanBooks()
  }

  function closeAddBook() {
    addBookStep = 1
    addedBook = null
    openAddBookModal = false
    loadMyPlanBooks()
  }

  function triggerSearch(e) {
    if (e.keyCode === 13) {
      getBooks(bookFilters, true)
    }
  }

  function updatePlanBook(plan_book: PlanBookType) {
    console.log("Updating plan book", plan_book)
    if (plan_book) {
      book = books.find(function (b) {
        if (b.id === plan_book.book_id) {
          return true
        }
      })
      book = { ...book, plan_book: plan_book }
      let book_index = books.findIndex((b) => b.id == book.id)
      if (bookFilters.plan_filter === "read_later") {
        if (!plan_book.read_later) {
          books.splice(book_index, 1)
          books = books
          return
        }
      }
      books[book_index] = book
      books = books
    }
  }
</script>

<Layout {current_user} {plan} {plan_users} {onboarding} {app_banner}>
  <div
    class="bg-gradient-to-r from-course-bg-light to-course-bg-dark w-full border-t-border-b-2 border-gray-800"
  >
    <div
      class="mx-auto max-w-7xl grid grid-cols-1 md:grid-cols-3 items-center sm:px-6 lg:px-8"
    >
      <div class="flex justify-start items-center">
        <a
          href="/plans/{appStore.plan.id}/university"
          class="flex items-center gap-1 justify-start hover:border-b hover:font-bold text-white"
        >
          <ArrowLeft class="size-6 text-white" />
          Back to University
        </a>
      </div>
      <div class="py-3">
        <div class="flex items-center justify-center">
          <img
            class="inline-block h-6 w-6 rounded-full shadow bg-gray-50 p-1"
            src={plan.logo_url}
            alt="Logo"
          />
          <p
            class="text-base leading-tight text-gray-200 ml-2"
            data-name="plan-name"
          >
            {plan.name}
          </p>
        </div>
        <h1 class="text-white text-2xl text-center mt-1">Bookshelf</h1>
      </div>
      <div class="py-3">
        {#if admin_university}
          <div class="flex items-center justify-end">
            <button
              class="mt-2 py-3 px-4 xl:px-8 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 hover:font-bold"
              onclick={() => (openAddBookModal = true)}
            >
              Add Your Own Book
            </button>
          </div>
        {/if}
      </div>
    </div>
  </div>

  <div class="w-full md:max-w-5xl mx-auto lg:max-w-7xl">
    <div class="grid grid-cols-4 gap-6">
      <div class="bg-white px-4 py-12">
        <div class="grid grid-cols-1 gap-y-6">
          <div>
            <h2 class="text-2xl font-bold">Bookshelf</h2>
          </div>
          <div>
            <div>
              <label for="search" class="sr-only">Search</label>
              <div class="relative mt-1 rounded-md shadow-sm">
                <div
                  class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
                  aria-hidden="true"
                >
                  <svg
                    class="mr-4 h-5 w-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  bind:value={bookFilters.search_query}
                  onkeydown={triggerSearch}
                  onblur={() => getBooks(bookFilters, true)}
                  id="search_query"
                  class="block w-full py-3 text-lg rounded-md border-gray-300 pl-10 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Search for Books"
                />
              </div>
            </div>
          </div>
          <div>
            <div class="relative flex items-start">
              <div class="flex h-5 items-center">
                <button
                  type="button"
                  id="show-completed"
                  class="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  role="switch"
                  onclick={updateCompletedFilter}
                  aria-checked="false"
                >
                  <span class="sr-only">Use setting</span>
                  <span
                    aria-hidden="true"
                    class="pointer-events-none absolute h-full w-full rounded-md bg-white"
                  ></span>
                  <!-- Enabled: "bg-indigo-600", Not Enabled: "bg-gray-200" -->
                  <span
                    aria-hidden="true"
                    class="{bookFilters.completed
                      ? 'bg-indigo-600'
                      : 'bg-gray-200'} pointer-events-none absolute mx-auto h-4 w-9 rounded-full transition-colors duration-200 ease-in-out"
                  ></span>
                  <!-- Enabled: "translate-x-5", Not Enabled: "translate-x-0" -->
                  <span
                    aria-hidden="true"
                    class="{bookFilters.completed
                      ? 'translate-x-5'
                      : 'translate-x-0'} pointer-events-none absolute left-0 inline-block h-5 w-5 transform rounded-full border border-gray-200 bg-white shadow ring-0 transition-transform duration-200 ease-in-out"
                  ></span>
                </button>
              </div>
              <div class="ml-3 text-sm">
                <label
                  for="show-completed"
                  class="font-medium text-gray-700 mt-0 pt-0"
                  >Only show read books</label
                >
              </div>
            </div>
          </div>
          <div>
            <h3 class="text-lg font-bold mb-2">Category</h3>
            <fieldset class="space-y-3">
              {#each categories as category}
                <div class="relative flex items-start">
                  <div class="flex h-5 items-center">
                    <input
                      id="category_{category}"
                      bind:group={selectedCategories}
                      onchange={() => updateCategoryFilter()}
                      value={category}
                      type="checkbox"
                      class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </div>
                  <div class="ml-3 text-sm">
                    <label
                      for="category_{category}"
                      class="font-medium text-gray-700 mt-0 pt-0"
                      >{category}</label
                    >
                  </div>
                </div>
              {/each}
            </fieldset>
          </div>
          <div>
            <h3 class="text-lg font-bold mb-2">Values</h3>
            <fieldset class="space-y-3">
              {#each values as value}
                <div class="relative flex items-start">
                  <div class="flex h-5 items-center">
                    <input
                      id="value_{value}"
                      bind:group={selectedValues}
                      onchange={() => updateValueFilter()}
                      {value}
                      type="checkbox"
                      class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                  </div>
                  <div class="ml-3 text-sm">
                    <label
                      for="value_{value}"
                      class="font-medium text-gray-700 mt-0 pt-0">{value}</label
                    >
                  </div>
                </div>
              {/each}
            </fieldset>
          </div>
          <div>
            {#if admin_university}
              <div class="border-t border-gray-200 mt-6">
                <div class="mt-6 flex flex-col justify-center px-2 xl:px-6">
                  <p class="text-lg font-bold mx-auto px-2 xl:px-6 text-center">
                    Don't see a book listed here?
                  </p>

                  <button
                    class="mt-2 py-3 px-4 xl:px-8 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 hover:font-bold"
                    onclick={() => (openAddBookModal = true)}
                    >Add Your Own Book</button
                  >
                </div>
              </div>
            {/if}
          </div>
        </div>
      </div>
      <div class="col-span-3 pr-4">
        <section class="max-w-3xl mx-auto lg:max-w-7xl py-6">
          <div class="grid grid-cols-2">
            <div class="flex items-center justify-start">
              <div
                class="bg-lwp-blue text-gray-200 rounded-lg flex p-1 gap-x-1"
                id="tab-selector"
              >
                <button
                  type="button"
                  onclick={() => updatePlanFilter("all")}
                  class="p-2 px-4 rounded-tl-lg rounded-bl-lg text-base hover:shadow-lg"
                  class:active={bookFilters.plan_filter === "all"}
                >
                  All Books
                </button>
                <button
                  type="button"
                  onclick={() => updatePlanFilter("read_later")}
                  class="p-2 px-4 text-base hover:shadow-lg"
                  class:active={bookFilters.plan_filter === "read_later"}
                >
                  Read Later
                </button>
                <button
                  type="button"
                  onclick={() => updatePlanFilter("plan")}
                  class="p-2 px-4 rounded-tr-lg rounded-br-lg text-base hover:shadow-lg"
                  class:active={bookFilters.plan_filter === "plan"}
                >
                  My Plan Books
                </button>
              </div>
            </div>
          </div>
        </section>

        <section class="max-w-3xl mx-auto lg:max-w-7xl py-6">
          <div
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            data-name="book-list"
            id="infinite-scroll"
          >
            {#key books}
              {#each books as book (book.id)}
                <Book {book} onview={viewBook} />
              {/each}
            {/key}

            {#if isLoading === false && books.length === 0}
              <div class="md:col-span-2 lg:col-span-3 flex justify-center">
                <div
                  class="max-w-xl bg-yellow-100 px-6 py-8 border-yellow-200 rounded-lg shadow"
                >
                  <p class="text-xl font-bold text-gray-700">
                    {#if bookFilters.plan_filter === "plan"}
                      Your plan has not added any books yet.
                    {:else if bookFilters.plan_filter === "read_later"}
                      You have not added any books to your read later list yet.
                    {:else}
                      No Books found that match your filters.
                    {/if}
                  </p>
                </div>
              </div>
            {/if}
          </div>
          {#if isLoading}
            <div class="py-6 w-full">
              <div class="text-center flex justify-center">
                <Pulse size="140" color="#cdcdcd" unit="px" duration="1s" />
              </div>
            </div>
          {/if}
        </section>
      </div>
    </div>
  </div>

  <Modal bind:open={openBookModal} large={true}>
    <ViewBook book_id={book.id} onplanbook={updatePlanBook} />
  </Modal>

  <Modal bind:open={openAddBookModal}>
    {#if addBookStep === 1}
      <BookForm {categories} {values} onadd={addNewBook} />
    {:else if addBookStep === 2 && addedBook}
      <BookReadingsForm bind:book={addedBook} onadd={addedBookReadings} />
    {/if}
  </Modal>

  <style lang="css">
    #tab-selector button {
      font-weight: 400;
      outline: none;
      transition: background-color 200ms ease-in-out;
      background-color: #4a4fdf;
    }
    #tab-selector button:hover {
      background-color: #3c39c0;
      font-weight: 600;
    }
    #tab-selector button.active {
      background-color: white;
      color: #222;
      font-weight: 700;
      z-index: 5;
    }
  </style>
</Layout>
